import { type ReactNode, type RefObject, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, X } from 'lucide-react'
import { TOKENS } from '../tokens'
import { tabbableElements, useOverlay } from './useOverlay'
import { LAYERS, OverlayLayer, useOverlayLayer } from './overlayLayer'

export interface DialogProps {
  open: boolean
  /** Names the dialog. */
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  /** Buttons along the bottom, for example Cancel and Save. They stay in view while the content scrolls. */
  footer?: ReactNode
  /** Shown inside the dialog, above the content, with role="alert", so it can't hide behind the overlay. */
  error?: ReactNode
  /** Escape, the close button and (while `dismissible`) the backdrop call this. */
  onClose: () => void
  /** A request is running: Escape, the backdrop and the close button do nothing. */
  busy?: boolean
  /**
   * A click on the backdrop closes the dialog. Pass false for forms, so a stray
   * click can't throw away what was typed; Escape and the close button still close it.
   */
  dismissible?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /** Where focus starts. Default: the first field or button in the content, then in the footer. */
  initialFocus?: RefObject<HTMLElement>
}

const WIDTHS = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl', '2xl': 'max-w-4xl' } as const

/**
 * A modal dialog: on document.body, above drawers (the same layer as
 * ConfirmDialog) and above any overlay it was opened from, named by its
 * title. Escape and the close button close it, focus moves in and stays in,
 * and goes back to the opener afterwards.
 */
export function Dialog({
  open,
  title,
  description,
  children,
  footer,
  error,
  onClose,
  busy = false,
  dismissible = true,
  size = 'md',
  initialFocus,
}: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const contentRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const zIndex = useOverlayLayer(LAYERS.dialog)
  const panelRef = useOverlay<HTMLDivElement>({
    open,
    onClose,
    busy,
    initialFocus: () =>
      initialFocus?.current ?? tabbableElements(contentRef.current)[0] ?? tabbableElements(footerRef.current)[0],
  })

  if (!open) return null

  return createPortal(
    <OverlayLayer zIndex={zIndex}>
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex }}>
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          aria-hidden
          data-testid="dialog-backdrop"
          // Pressing the backdrop mustn't take focus out of the dialog.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (dismissible && !busy) onClose()
          }}
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          tabIndex={-1}
          className={`relative w-full ${WIDTHS[size]} max-h-[90vh] flex flex-col rounded-2xl shadow-xl outline-none ${TOKENS.bgCard} ${TOKENS.border}`}
        >
          <div className="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1 px-6 pt-5 pb-4 border-b border-border shrink-0">
            <h2 id={titleId} className="font-display font-bold text-lg text-foreground">
              {title}
            </h2>
            <button
              type="button"
              aria-label="Close"
              disabled={busy}
              onClick={onClose}
              className="-mr-1.5 rounded-lg p-1.5 text-foreground-muted hover:bg-surface-hover hover:text-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X size={18} aria-hidden />
            </button>
            {description ? (
              <div id={descriptionId} className={`col-start-1 text-sm ${TOKENS.textSecondary}`}>
                {description}
              </div>
            ) : null}
          </div>
          {error ? (
            <div
              role="alert"
              className="mx-6 mt-4 flex items-start gap-2 rounded-xl border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-foreground shrink-0"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-danger" aria-hidden />
              <div className="min-w-0">{error}</div>
            </div>
          ) : null}
          {children != null && children !== false ? (
            <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-5">
              {children}
            </div>
          ) : null}
          {footer ? (
            <div ref={footerRef} className="flex flex-wrap justify-end gap-2 px-6 py-4 border-t border-border shrink-0">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </OverlayLayer>,
    document.body,
  )
}
